import type { Locale } from "@/types/nango"

// What each export column holds, shown behind the "i" of its row in the
// column picker. Keys mirror the backend builders (csv_export.round_columns /
// weekly_columns, lead_export_columns.flat_columns, the data_* payload
// columns): a key added there without an entry here simply shows no "i".

type Description = { FR: string; EN: string }

const EXACT: Record<string, Description> = {
  // ── Pitch sheet: the company ──
  name: { FR: "Nom de l'entreprise.", EN: "Company name." },
  website: { FR: "Site web de l'entreprise.", EN: "Company website." },
  normalized_domain: {
    FR: "Domaine du site, sans « https:// » ni « www » (ex. exemple.fr). Pratique pour dédoublonner.",
    EN: 'Website domain, without "https://" or "www" (e.g. example.com). Handy to deduplicate.',
  },
  address: { FR: "Adresse postale.", EN: "Postal address." },
  grade: {
    FR: "Note globale du lead : A, B ou C.",
    EN: "Overall lead grade: A, B or C.",
  },
  has_website: {
    FR: "True si l'entreprise a son propre site, False sinon.",
    EN: "True when the company has its own website, False otherwise.",
  },
  business_opportunity: {
    FR: "Rempli seulement sans site web : pas de site mais une fiche Google, ou aucune présence en ligne.",
    EN: "Only filled without a website: no site but a Google profile, or no online presence at all.",
  },
  google_profile_url: {
    FR: "Lien vers la fiche Google Maps de l'entreprise.",
    EN: "Link to the company's Google Maps profile.",
  },
  serp_query: {
    FR: "Recherche Google sur laquelle on a trouvé l'entreprise.",
    EN: "Google search the company was found on.",
  },
  serp_page: {
    FR: "Page de résultats Google où on l'a trouvée.",
    EN: "Google results page it was found on.",
  },
  serp_position: {
    FR: "Position sur cette page de résultats.",
    EN: "Position on that results page.",
  },
  fair: {
    FR: "Salon où l'entreprise a été trouvée (campagnes salons).",
    EN: "Trade show the company was found at (trade-show campaigns).",
  },
  company_id: {
    FR: "Numéro d'identification légal (SIREN en France).",
    EN: "Legal company number (SIREN in France).",
  },
  company_legal_name: {
    FR: "Raison sociale au registre du commerce.",
    EN: "Legal name in the company registry.",
  },
  niche: {
    FR: "Métier de l'entreprise tel que l'audit l'a compris (ex. « installateur store »).",
    EN: 'The trade the audit read the company as (e.g. "awning installer").',
  },
  delivered_at: {
    FR: "Date et heure de livraison du lead.",
    EN: "Date and time the lead was delivered.",
  },

  // ── Pitch sheet: the contact ──
  person_name: {
    FR: "Nom complet du contact de la ligne.",
    EN: "Full name of the row's contact.",
  },
  person_first_name: { FR: "Prénom du contact.", EN: "Contact's first name." },
  person_last_name: { FR: "Nom du contact.", EN: "Contact's last name." },
  person_role: {
    FR: "Fonction du contact (ex. Gérant).",
    EN: "Contact's role (e.g. Owner).",
  },
  round: {
    FR: "Vague d'envoi du contact : 1 pour le premier contact, 2 pour le suivant, etc.",
    EN: "Outreach round of the contact: 1 for the first contact, 2 for the next, etc.",
  },
  email: { FR: "E-mail du contact.", EN: "Contact's email." },
  phone: { FR: "Téléphone du contact.", EN: "Contact's phone." },

  // ── Pitch sheet: reviews ──
  rating: { FR: "Note Google (sur 5).", EN: "Google rating (out of 5)." },
  reviews_count: {
    FR: "Nombre d'avis Google.",
    EN: "Number of Google reviews.",
  },
  ratings_json: {
    FR: "Toutes les notes trouvées (Google et autres plateformes), en JSON.",
    EN: "Every rating found (Google and other platforms), as JSON.",
  },

  // ── Pitch sheet: website audit ──
  website_quality_score: {
    FR: "Score global du site, sur 100.",
    EN: "Overall website score, out of 100.",
  },
  technical_score: {
    FR: "Score technique du site (vitesse, SEO technique), sur 100.",
    EN: "Technical website score (speed, technical SEO), out of 100.",
  },
  commercial_score: {
    FR: "Score commercial du site (capacité à générer des demandes), sur 100.",
    EN: "Commercial website score (how well it brings in enquiries), out of 100.",
  },
  score_conversion: {
    FR: "Sous-score conversion : boutons devis, téléphone, formulaires visibles.",
    EN: "Conversion sub-score: quote buttons, phone, visible forms.",
  },
  score_content: {
    FR: "Sous-score contenu : richesse des pages, réalisations, pages locales.",
    EN: "Content sub-score: page depth, case studies, local pages.",
  },
  score_design: {
    FR: "Sous-score design : modernité et soin du site.",
    EN: "Design sub-score: how modern and polished the site is.",
  },
  score_contact_ux: {
    FR: "Sous-score contact : facilité à joindre l'entreprise depuis le site.",
    EN: "Contact sub-score: how easy it is to reach the company from the site.",
  },
  score_cms: {
    FR: "Sous-score CMS / technologie du site.",
    EN: "CMS / tech stack sub-score.",
  },
  score_schema: {
    FR: "Sous-score balisage local (données structurées lues par Google).",
    EN: "Local schema sub-score (structured data Google reads).",
  },
  audit_opportunities: {
    FR: "Points faibles relevés par l'audit, en résumé : « thème:titre | … ».",
    EN: 'Weak points the audit found, summarised: "theme:title | …".',
  },
  audit_strengths: {
    FR: "Signaux d'achat relevés (plusieurs agences, clientèle pro…), en résumé.",
    EN: "Buying signals found (several locations, B2B clients…), summarised.",
  },
  google_rank: {
    FR: "Position Google mesurée sur la recherche principale.",
    EN: "Measured Google position on the main search.",
  },
  google_rank_query: {
    FR: "Recherche Google sur laquelle cette position a été mesurée.",
    EN: "Google search that position was measured on.",
  },
  geo_visibility: {
    FR: "Villes voisines où le site sort en page 1, ex. « 2/5 ».",
    EN: 'Nearby towns where the site shows on page 1, e.g. "2/5".',
  },
  keyword_visibility: {
    FR: "Recherches métier où le site sort en page 1, ex. « 3/10 ».",
    EN: 'Trade searches where the site shows on page 1, e.g. "3/10".',
  },
  ai_cited_questions: {
    FR: "Questions clients où ChatGPT / Gemini citent l'entreprise, ex. « 0/8 ».",
    EN: 'Buyer questions where ChatGPT / Gemini cite the company, e.g. "0/8".',
  },
  ai_competitors: {
    FR: "Concurrents que les IA recommandent à sa place.",
    EN: "Competitors the AIs recommend instead.",
  },

  // ── Pitch sheet: other plugins ──
  has_ads: {
    FR: "« yes » si une pub active a été trouvée sur une des régies vérifiées, « no » sinon, vide si non vérifié.",
    EN: '"yes" when a running ad was found on one of the checked registries, "no" otherwise, empty when unchecked.',
  },
  ad_platforms: {
    FR: "Régies où une pub active a été trouvée.",
    EN: "Registries where a running ad was found.",
  },
  ads_meta_ad_library: {
    FR: "Pubs Facebook / Instagram (Meta) : yes, no, date de la dernière pub si arrêtée, ou unchecked. Vide si non vérifié.",
    EN: "Facebook / Instagram (Meta) ads: yes, no, last ad date when stopped, or unchecked. Empty when not checked.",
  },
  ads_google_ads: {
    FR: "Pubs Google Ads : yes, no, date de la dernière pub si arrêtée, ou unchecked. Vide si non vérifié.",
    EN: "Google Ads: yes, no, last ad date when stopped, or unchecked. Empty when not checked.",
  },
  ads_tiktok_ad_library: {
    FR: "Pubs TikTok : yes, no, date de la dernière pub si arrêtée, ou unchecked. Vide si non vérifié.",
    EN: "TikTok ads: yes, no, last ad date when stopped, or unchecked. Empty when not checked.",
  },
  ads_pages_jaunes: {
    FR: "Encart payant Pages Jaunes : yes, no, ou unchecked. Vide si non vérifié.",
    EN: "Paid Pages Jaunes listing: yes, no, or unchecked. Empty when not checked.",
  },
  job_offers: {
    FR: "Offres d'emploi en ligne : « Intitulé (lien) ; … ».",
    EN: 'Open job offers: "Title (link) ; …".',
  },
  custom_enrichment: {
    FR: "Réponse de l'agent à votre question personnalisée.",
    EN: "The agent's answer to your custom question.",
  },
  custom_enrichment_lead: {
    FR: "Réponse à votre question personnalisée, sur l'entreprise.",
    EN: "Answer to your custom question, about the company.",
  },
  custom_enrichment_person: {
    FR: "Réponse à votre question personnalisée, sur le contact de la ligne.",
    EN: "Answer to your custom question, about the row's contact.",
  },

  // ── Audit details ──
  rank_geo_detail: {
    FR: "Chaque recherche « métier + ville voisine » testée, avec la position : « requête:#3 p1 | … ».",
    EN: 'Every "trade + nearby town" search tested, with the position: "query:#3 p1 | …".',
  },
  rank_keyword_detail: {
    FR: "Chaque recherche métier testée, avec la position : « requête:#3 p1 | … ».",
    EN: 'Every trade search tested, with the position: "query:#3 p1 | …".',
  },
  rank_checked_at: {
    FR: "Date de mesure des positions Google.",
    EN: "Date the Google positions were measured.",
  },
  audit_opportunities_detail: {
    FR: "Points faibles en détail : thème, gravité, titre et ce qui a été vu sur le site.",
    EN: "Weak points in full: theme, severity, title and what was seen on the site.",
  },
  audit_strengths_detail: {
    FR: "Signaux d'achat en détail, avec ce qui a été vu sur le site.",
    EN: "Buying signals in full, with what was seen on the site.",
  },
  ratings_detail: {
    FR: "Notes par plateforme, lisibles (Google, etc.).",
    EN: "Ratings per platform, readable (Google, etc.).",
  },
  audit_version: {
    FR: "Version de l'audit qui a produit ces données (technique).",
    EN: "Version of the audit that produced this data (technical).",
  },
  breakdown_total: {
    FR: "Total brut des critères de l'audit avant pondération (technique).",
    EN: "Raw total of the audit criteria before weighting (technical).",
  },
  niche_pitch: {
    FR: "Argumentaire du métier : pourquoi l'offre marche, objections attendues.",
    EN: "Trade pitch: why the offer works, expected objections.",
  },
  pagespeed_performance: {
    FR: "Score de vitesse Google PageSpeed.",
    EN: "Google PageSpeed performance score.",
  },
  deterministic_unavailable: {
    FR: "Rempli quand l'analyse automatique du site n'a pas pu tourner (technique).",
    EN: "Filled when the automated site scan could not run (technical).",
  },
  deterministic_attempts: {
    FR: "Nombre d'essais de l'analyse automatique du site (technique).",
    EN: "Number of attempts of the automated site scan (technical).",
  },
  serp_proof_url: {
    FR: "Lien de la page de résultats Google où on a trouvé l'entreprise.",
    EN: "Link to the Google results page the company was found on.",
  },
}

// Numbered and per-platform columns, matched on their shape.
const PATTERNS: [RegExp, (match: RegExpMatchArray) => Description][] = [
  [
    /^seo_kw(\d+)$/,
    ([, n]) => ({
      FR: `Recherche Google n°${Number(n)} mesurée par l'audit : mot-clé · volume mensuel · position trouvée · pourquoi.`,
      EN: `Google search #${Number(n)} measured by the audit: keyword · monthly volume · position found · why.`,
    }),
  ],
  [
    /^step(\d+)_subject$/,
    ([, n]) => ({
      FR: `Objet de l'e-mail ${n} rédigé pour ce contact.`,
      EN: `Subject of email ${n} drafted for this contact.`,
    }),
  ],
  [
    /^step(\d+)_body$/,
    ([, n]) => ({
      FR: `Corps de l'e-mail ${n} rédigé pour ce contact.`,
      EN: `Body of email ${n} drafted for this contact.`,
    }),
  ],
  [
    /^li(\d+)_body$/,
    ([, n]) => ({
      FR: `Message LinkedIn ${n} rédigé pour ce contact.`,
      EN: `LinkedIn message ${n} drafted for this contact.`,
    }),
  ],
  [
    /^ig(\d+)_body$/,
    ([, n]) => ({
      FR: `Message Instagram ${n} rédigé pour ce contact.`,
      EN: `Instagram message ${n} drafted for this contact.`,
    }),
  ],
  [
    /^wa(\d+)_body$/,
    ([, n]) => ({
      FR: `Message WhatsApp ${n} rédigé pour ce contact.`,
      EN: `WhatsApp message ${n} drafted for this contact.`,
    }),
  ],
  [
    /^call(\d+)_body$/,
    ([, n]) => ({
      FR: `Script de l'appel ${n} rédigé pour ce contact.`,
      EN: `Script of call ${n} drafted for this contact.`,
    }),
  ],
  [
    /^call(\d+)_pain_points$/,
    ([, n]) => ({
      FR: `Points de douleur à aborder pendant l'appel ${n}.`,
      EN: `Pain points to raise during call ${n}.`,
    }),
  ],
  [
    /^social_(.+)$/,
    ([, platform]) => ({
      FR: `Lien du profil ${platform} de l'entreprise.`,
      EN: `Link to the company's ${platform} profile.`,
    }),
  ],
  [
    /^place_(.+)$/,
    ([, field]) => ({
      FR: `Champ « ${field} » de l'établissement d'origine trouvé par la recherche (technique).`,
      EN: `Field "${field}" of the original place the search found (technical).`,
    }),
  ],
  [
    /^data_(.+)$/,
    ([, field]) => ({
      FR: `Valeur brute du champ « ${field} » de la fiche lead, telle que stockée (JSON pour les objets et listes).`,
      EN: `Raw value of the lead record's "${field}" field, as stored (JSON for objects and lists).`,
    }),
  ],
]

export function columnDescription(key: string, locale: Locale): string | null {
  const exact = EXACT[key]
  if (exact) return exact[locale]
  for (const [pattern, describe] of PATTERNS) {
    const match = key.match(pattern)
    if (match) return describe(match)[locale]
  }
  return null
}
